/* __next_internal_client_entry_do_not_use__ Checkbox,default auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Typography, TypographyStyles } from "@faire/web--source/slate/Typography";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { css, useTheme } from "styled-components";
import { Checkmark as CheckmarkIcon } from "../icons/deprecated/Checkmark";
import { IntermediateCheckmark } from "../icons/IntermediateCheckmark";
import { Row } from "../Layout";
import { highVisSlateComponent, HIGH_VIS_FILTER } from "../utils/highVisSlateComponent";
import { getCheckboxColors } from "./utils";
/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/checkbox)
 */ export var Checkbox = function(_param) {
    var children = _param.children, props = _object_without_properties(_param, [
        "children"
    ]);
    var theme = useTheme();
    var colors = getCheckboxColors(props, theme);
    var align = props.align, ariaLabel = props["aria-label"], isChecked = props.checked, className = props.className, dataTestId = props["data-test-id"], disabled = props.disabled, iconProps = props.iconProps, id = props.id, tmp = props.intermediate, isIntermediate = tmp === void 0 ? false : tmp, label = props.label, margin = props.margin, name = props.name, onBlur = props.onBlur, onChange = props.onChange, secondaryLabel = props.secondaryLabel, _props_size = props.size, size = _props_size === void 0 ? 16 : _props_size, value = props.value;
    var ariaChecked;
    if (isChecked) {
        ariaChecked = "true";
    } else if (isIntermediate) {
        ariaChecked = "mixed";
    } else {
        ariaChecked = "false";
    }
    var labelId = useUniqueId("checkbox-label-");
    var checkboxId = id !== null && id !== void 0 ? id : labelId;
    return /*#__PURE__*/ _jsxs(CheckboxWrapper, {
        $align: align,
        $checked: isChecked || isIntermediate,
        className: className,
        $colors: colors,
        $disabled: disabled,
        $size: size,
        theme: theme,
        children: [
            /*#__PURE__*/ _jsx("input", {
                "aria-label": ariaLabel,
                "aria-checked": ariaChecked,
                checked: isChecked || isIntermediate,
                "data-test-id": dataTestId,
                disabled: disabled,
                id: checkboxId,
                name: name,
                onBlur: onBlur,
                onChange: onChange,
                type: "checkbox",
                value: value
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "visible-wrapper",
                children: [
                    /*#__PURE__*/ _jsx(Checkmark, {
                        $checked: isChecked || isIntermediate,
                        $colors: colors,
                        $disabled: disabled,
                        $margin: margin,
                        role: "presentation",
                        $size: size,
                        theme: theme,
                        children: isChecked ? /*#__PURE__*/ _jsx(CheckmarkIcon, _object_spread({
                            "aria-hidden": true,
                            fontSize: "".concat(size, "px")
                        }, iconProps !== null && iconProps !== void 0 ? iconProps : {})) : /*#__PURE__*/ _jsx(IntermediateCheckmark, _object_spread({
                            "aria-hidden": true,
                            fontSize: "".concat(size, "px")
                        }, iconProps !== null && iconProps !== void 0 ? iconProps : {}))
                    }),
                    children || label ? /*#__PURE__*/ _jsxs(Label, {
                        $checked: isChecked || isIntermediate,
                        $colors: colors,
                        $disabled: disabled,
                        htmlFor: checkboxId,
                        theme: theme,
                        children: [
                            /*#__PURE__*/ _jsxs(Row, {
                                children: [
                                    label,
                                    children
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                color: Core.text.subdued,
                                children: secondaryLabel
                            })
                        ]
                    }) : null
                ]
            })
        ]
    });
};
var Checkmark = styled.div.withConfig({
    componentId: "sc-f9074fb3-0"
})([
    "align-items:center;background-color:",
    ";border:1px solid ",
    ";box-sizing:border-box;color:",
    ";cursor:",
    ";display:flex;justify-content:center;margin:",
    ";margin-block:2px;",
    ";"
], function(param) {
    var $checked = param.$checked, $colors = param.$colors, $disabled = param.$disabled;
    return $checked || $disabled ? $colors.fill : "transparent";
}, function(param) {
    var $colors = param.$colors;
    return $colors.border;
}, function(param) {
    var $checked = param.$checked, $colors = param.$colors;
    return $checked ? $colors.icon : "transparent";
}, function(param) {
    var $disabled = param.$disabled;
    return $disabled ? "auto" : "pointer";
}, function(param) {
    var $margin = param.$margin;
    return $margin !== null && $margin !== void 0 ? $margin : "0";
}, function(param) {
    var $size = param.$size;
    return css([
        "flex:0 0 ",
        "px;height:",
        "px;line-height:",
        "px;width:",
        "px;"
    ], $size, $size, $size, $size);
});
var Label = styled.label.withConfig({
    componentId: "sc-f9074fb3-1"
})([
    "cursor:",
    ";padding:0 0 0 8px !important;",
    " text-align:left;"
], function(param) {
    var $disabled = param.$disabled;
    return $disabled ? "auto" : "pointer";
}, function(param) {
    var $checked = param.$checked, $colors = param.$colors, theme = param.theme;
    var _ref;
    return TypographyStyles((_ref = $checked ? _object_spread_props(_object_spread({}, $colors.checked), {
        theme: theme
    }) : _object_spread_props(_object_spread({}, $colors.base), {
        theme: theme
    })) !== null && _ref !== void 0 ? _ref : {
        theme: theme
    });
});
var CheckboxWrapper = styled.div.withConfig({
    componentId: "sc-f9074fb3-2"
})([
    "",
    ";align-items:",
    ";display:flex;justify-content:flex-start;position:relative;input{position:absolute;left:0;",
    ";&:hover{cursor:",
    ";}}input:focus-visible + .visible-wrapper{background-color:Highlight;outline:5px auto Highlight;outline:5px auto -webkit-focus-ring-color;}.visible-wrapper{align-items:",
    ";display:flex;}&:hover{",
    "{",
    "}",
    "{border:1px solid ",
    ";}}",
    ""
], function(param) {
    var theme = param.theme;
    return TypographyStyles({
        theme: theme
    });
}, function(param) {
    var $align = param.$align;
    return $align !== null && $align !== void 0 ? $align : "center";
}, function(param) {
    var $size = param.$size;
    return css([
        "height:",
        "px;width:",
        "px;"
    ], $size, $size);
}, function(param) {
    var $disabled = param.$disabled;
    return $disabled ? "auto" : "pointer";
}, function(param) {
    var $align = param.$align;
    return $align !== null && $align !== void 0 ? $align : "center";
}, Label, function(param) {
    var $colors = param.$colors, theme = param.theme;
    var _ref;
    return TypographyStyles((_ref = _object_spread({
        theme: theme
    }, $colors.hover)) !== null && _ref !== void 0 ? _ref : {
        theme: theme
    });
}, Checkmark, function(param) {
    var $colors = param.$colors;
    return $colors.borderActive;
}, highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
export default Checkbox;
